import { h, Component, Element, Prop, Watch, Event } from '@stencil/core';
import type { ComponentInterface, EventEmitter } from '@stencil/core';

@Component({
	tag: 'app-dialog',
	styleUrl: 'dialog.scss'
})
export class Dialog implements ComponentInterface {
	@Element() root: HTMLElement;
	dialog?: HTMLDialogElement;

	/** If the component is modal, the rest of the page is inert */
	@Prop({ reflect: true }) modal = false;
	/** Whether the dialog is open */
	@Prop({ reflect: true, mutable: true }) open = false;
	@Prop() dialogId?: string;

	@Event() appDialogReturn: EventEmitter<string>;

	private async reflectFromOpenProp() {
		if (this.dialog?.open === this.open || !this.dialog?.isConnected)
			return;

		if (this.open) {
			if (this.modal)
				this.dialog!.showModal();
			else
				this.dialog!.show();
		}
		else {
			this.dialog!.classList.add('closing');
			await Promise.allSettled(
				this.dialog!.getAnimations({ subtree: true })
					.map(animation => animation.finished)
			);
			this.dialog!.classList.remove('closing');
			// could have changed during await
			if (this.dialog!.open)
				this.dialog!.close();
		}
	}

	@Event() appDialogToggled: EventEmitter<boolean>;
	@Watch('open')
	onOpenChange() {
		this.reflectFromOpenProp();
		this.appDialogToggled.emit(this.open);
	}

	private getTriggers() {
		if (!this.dialogId)
			return;

		const rootNode = this.root.getRootNode() as ParentNode;
		return new Set(
			[...rootNode.querySelectorAll('[aria-controls]')]
				.filter(element => element.getAttribute('aria-controls') === this.dialogId)
		);
	}

	componentWillLoad() {
		addEventListener('click', e => {
			if (!this.dialogId)
				return;

			const triggers = this.getTriggers();
			if (triggers && e.composedPath().some(node => triggers.has(node as any)))
				this.open = !this.open;
		});

		addEventListener('keydown', e => {
			if (this.modal && this.open && e.key === 'Escape') {
				e.preventDefault();
				this.open = false;
			}
		});
	}

	render() {
		return (
			<dialog
				id={this.dialogId}
				ref={e => this.dialog = e}
				onClose={() => {
					this.open = false;
					this.appDialogReturn.emit(this.dialog!.returnValue);
				}}
				onClick={e => {
					const clicked = e.target as Element;
					const transparentToBackground = getComputedStyle(clicked).getPropertyValue('--transparent-to-backdrop') === 'true';
					if (this.dialog === clicked || transparentToBackground)
						this.open = false;
				}}
				onSubmit={(e: SubmitEvent) => {
					const form = e.target as HTMLFormElement;
					if (((e.submitter as any).formMethod || form.method) === 'dialog') {
						e.preventDefault();
						const value = (e.submitter as any).value;
						if (value)
							this.dialog!.returnValue = value;
						this.open = false;
					}
				}}
			>
				<div class='dialog'>
					<slot name='content'>
						<slot name='header' key='header'>
							<header class='dialog__fallback-header'>
								<slot name='title' key='title' />

								<slot name='close' key='close'>
									<form method='dialog'>
										<button value='close'>
											<slot name='close-icon'>
												<app-icon icon='close-thin' />
											</slot>
											<slot name='close-label'>
												<span class='visually-hidden'>Close</span>
											</slot>
										</button>
									</form>
								</slot>
							</header>
						</slot>

						<div class='dialog__body'>
							<slot />
						</div>

						<slot name='footer' key='footer' />
					</slot>
				</div>
			</dialog>
		);
	}

	async componentDidRender() {
		await this.reflectFromOpenProp();
		this.getTriggers()?.forEach(element => {
			element.setAttribute('aria-expanded', this.open + '');
		});
	}
}
